import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useUser } from '../../hooks/use-user';
import { JobFilterSelectableEntities as CustomerJobFilterSelectableEntities } from '../../lib/interfaces/customers/jobs';
import { JobFilterSelectableEntities as InternalJobFilterSelectableEntities } from '../../lib/interfaces/internal/jobs';
import { UserRole, UserType } from '../../lib/interfaces/user';
import AddIcon from '../../ui/components/icons/add-icon';
import DotIcon from '../../ui/components/icons/dot-icon';
import FilterIcon from '../../ui/components/icons/filter-icon';
import { Button } from '../../ui/components/ui/button';
import DataFetchParent from '../../ui/components/ui/data-fetch';
import NewOrder from '../../ui/components/ui/new-order';
import SearchInput from '../../ui/components/ui/search-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/components/ui/select';
import { Context } from '../root/context';
import CustomerJobFetchParent from './customer/job-fetcher';
import InternalJobFetchParent from './internal/job-fetcher';

const Order = () => {
  const [searchParams] = useSearchParams();
  const cachedOrganisation = localStorage.getItem('cached-organisation');
  const [searchQuery, setSearchQuery] = useState(
    searchParams.toString() || `statusFilter=ALL&page=1${cachedOrganisation ? `&organizationId=${cachedOrganisation}` : ''}`
  );
  const [customerSelectableEntities, setCustomerSelectableEntities] = useState<CustomerJobFilterSelectableEntities>();
  const [internalSelectableEntities, setInternalSelectableEntities] = useState<InternalJobFilterSelectableEntities>();
  const [initLoading, setInitLoading] = useState(true);
  const [resetPagination, setResetPagination] = useState(false);
  const [months, setMonths] = useState(0);
  const [warningState, setWarningState] = useState(false);
  const { user } = useUser();
  const { isMobile } = useContext(Context);

  useEffect(() => {
    if (customerSelectableEntities || internalSelectableEntities) {
      setInitLoading(false);
    }
  }, [customerSelectableEntities, internalSelectableEntities]);

  return (
    <div className="flex flex-col gap-5 h-full">
      <div className="flex justify-between">
        {user?.userType === UserType.Customer ? (
          <DataFetchParent
            isLoading={initLoading}
            data={customerSelectableEntities}
            renderElement={(data) => (
              <OrderCustomerFilters setSearchQuery={setSearchQuery} selectableEntities={data} setResetPagination={setResetPagination} />
            )}
          />
        ) : (
          <DataFetchParent
            isLoading={initLoading}
            data={internalSelectableEntities}
            renderElement={(data) => (
              <OrderInternalFilters
                setSearchQuery={setSearchQuery}
                selectableEntities={data}
                setResetPagination={setResetPagination}
                cachedOrganisation={cachedOrganisation ?? undefined}
              />
            )}
          />
        )}
        <div className="flex gap-3">
          {user?.userType === UserType.Internal && !isMobile && warningState && (
            <div className="flex justify-end items-center text-[8px]">
              <div className="border border-[#FC9701] bg-[#FC970133] text-[#FC9701] w-fit rounded-full px-8 h-fit py-2 self-center flex items-center gap-2">
                <p className="text-sm">Zahlung überfällig</p>
              </div>
            </div>
          )}
          {user?.userType === UserType.Internal && user.userRole === UserRole.User && !isMobile ? (
            months > 0 && (
              <div className="border border-[#888888] rounded-full px-8 h-fit py-2 self-center flex items-center gap-2 ">
                <p className="text-sm text-[#888888]">Monate: {months}</p>
              </div>
            )
          ) : (
            <NewOrder
              trigger={
                <Button className="flex gap-4">
                  <AddIcon />
                  <span>Neuer Auftrag</span>
                </Button>
              }
            />
          )}
        </div>
      </div>
      {user?.userType === UserType.Customer ? (
        <CustomerJobFetchParent
          searchQuery={searchQuery}
          setSelectableEntities={setCustomerSelectableEntities}
          resetPagination={resetPagination}
          setResetPagination={setResetPagination}
        />
      ) : (
        <InternalJobFetchParent
          searchQuery={searchQuery}
          setSelectableEntities={setInternalSelectableEntities}
          resetPagination={resetPagination}
          setResetPagination={setResetPagination}
          cachedOrganisation={cachedOrganisation ?? undefined}
          setMonths={setMonths}
          setWarningState={setWarningState}
        />
      )}
    </div>
  );
};

const OrderInternalFilters = ({
  setSearchQuery,
  selectableEntities,
  setResetPagination,
  cachedOrganisation
}: {
  setSearchQuery: Dispatch<SetStateAction<string>>;
  selectableEntities: InternalJobFilterSelectableEntities;
  setResetPagination: Dispatch<SetStateAction<boolean>>;
  cachedOrganisation?: string;
}) => {
  const [newFilter, setNewFilter] = useState(false);
  const [activeFilter, setActiveFilter] = useState(false);
  const [doneFilter, setDoneFilter] = useState(false);
  const [currentOrganization, setCurrentOrganization] = useState<string>(selectableEntities.organizationsFilterUsed ?? cachedOrganisation);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = useState(searchParams.get('textSearch') || '');
  const [firstRender, setFirstRender] = useState(true);
  const [filtersChanged, setFiltersChanged] = useState(false);

  const { isMobile } = useContext(Context);

  useEffect(() => {
    if (!firstRender) return;

    const statusFilter = searchParams.get('statusFilter') || 'ALL';
    const organizationId = searchParams.get('organizationId') || cachedOrganisation || '';
    const textSearch = searchParams.get('textSearch') || '';

    setNewFilter(statusFilter === 'NEW');
    setActiveFilter(statusFilter === 'ACTIVE');
    setDoneFilter(statusFilter === 'DONE');
    setCurrentOrganization(organizationId);
    setSearchText(textSearch);
    setFirstRender(false);
  }, [searchParams, cachedOrganisation, firstRender]);

  useEffect(() => {
    if (firstRender) return;

    setFiltersChanged(true);
  }, [newFilter, activeFilter, doneFilter, currentOrganization, searchText, firstRender]);

  // Sync state changes to `searchParams`
  useEffect(() => {
    if (firstRender) return;

    const params = new URLSearchParams();

    const statusFilter = newFilter ? 'NEW' : activeFilter ? 'ACTIVE' : doneFilter ? 'DONE' : 'ALL';

    params.set('statusFilter', statusFilter);
    if (searchText) params.set('textSearch', searchText);
    params.set('page', filtersChanged ? '1' : searchParams.get('page') || '1');
    if (currentOrganization) params.set('organizationId', currentOrganization);

    // Update URL parameters only if they differ
    const queryString = params.toString();
    setSearchQuery(queryString);
    setSearchParams(params);
    setFiltersChanged(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersChanged, setSearchParams, setSearchQuery, firstRender, searchParams]);

  const hasAssignedOrganizations = selectableEntities.organizations.some((org) => org.isAssigned);

  return (
    <div className={`flex gap-4 ${isMobile ? 'flex-col w-full' : ''}`}>
      <SearchInput setSearchText={setSearchText} searchText={searchText} />
      <div className={`gap-3 ${isMobile ? 'grid grid-cols-2' : 'flex'}`}>
        <Button
          variant={doneFilter ? 'default' : 'outline'}
          onClick={() => {
            setDoneFilter((prev) => !prev);
            setNewFilter(false);
            setActiveFilter(false);
          }}
          className="flex gap-3 items-center rounded-2xl px-3 py-2 border text-sm ">
          <FilterIcon active={doneFilter} />
          Fertige Aufträge
        </Button>
        <Button
          variant={activeFilter ? 'default' : 'outline'}
          onClick={() => {
            setActiveFilter((prev) => !prev);
            setNewFilter(false);
            setDoneFilter(false);
          }}
          className="flex gap-3 items-center rounded-2xl px-3 py-2 border text-sm ">
          <FilterIcon active={activeFilter} />
          Offene Aufträge
        </Button>
        <Button
          variant={newFilter ? 'default' : 'outline'}
          onClick={() => {
            setNewFilter((prev) => !prev);
            setActiveFilter(false);
            setDoneFilter(false);
          }}
          className="flex gap-3 items-center rounded-2xl px-3 py-2 border text-sm ">
          <FilterIcon active={newFilter} />
          Neue Aufträge
        </Button>
      </div>
      <div className="relative">
        <Select
          onValueChange={(value) => {
            setCurrentOrganization(value);
            setResetPagination(true);
            localStorage.setItem('cached-organisation', value);
          }}
          defaultValue={currentOrganization}>
          <SelectTrigger className="gap-3 h-10 items-center rounded-2xl px-3 py-2 border text-sm inline-flex justify-center whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border-input bg-background hover:bg-accent hover:text-accent-foreground">
            <SelectValue placeholder="Kunde" />
          </SelectTrigger>
          <SelectContent className="">
            {hasAssignedOrganizations && (
              <div className="bg-[#F1F3F9] px-2 py-1 rounded-l mb-2">
                {selectableEntities.organizations
                  .filter((a) => a.isAssigned)
                  .map((user, i) => {
                    return (
                      <SelectItem key={user._id} value={user._id} className={`focus:bg-white w-full h-full font-bold flex items-center`}>
                        <div className="flex items-center gap-2">
                          {user.value}{' '}
                          {user.isTemporaryAssigned && <span className="bg-blue-500 rounded-full w-2 h-2 inline-block flex-shrink-0"></span>}
                          {user.warningState ? (
                            <span className="text-[#FC9701] inline-flex items-center align-middle ml-1.5">
                              <DotIcon className="w-1.5 h-1.5 flex-shrink-0 align-middle" />
                            </span>
                          ) : (
                            ''
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
              </div>
            )}
            <div className="">
              {selectableEntities.organizations
                .filter((a) => !a.isAssigned)
                .map((user, i) => {
                  return (
                    <SelectItem
                      key={user._id}
                      value={user._id}
                      className={`${i === selectableEntities.organizations.length - 1 ? '' : 'mb-1'} w-full h-full p-2 flex items-center`}>
                      <div className="flex items-center gap-2">
                        {user.value}{' '}
                        {user.isTemporaryAssigned && <span className="bg-blue-500 rounded-full w-2 h-2 inline-block flex-shrink-0"></span>}
                        {user.warningState ? (
                          <span className="text-[#FC9701] inline-flex items-center align-middle ml-1.5">
                            <DotIcon className="w-1.5 h-1.5 flex-shrink-0 align-middle" />
                          </span>
                        ) : (
                          ''
                        )}
                      </div>
                    </SelectItem>
                  );
                })}
            </div>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

const OrderCustomerFilters = ({
  setSearchQuery,
  selectableEntities,
  setResetPagination
}: {
  setSearchQuery: Dispatch<SetStateAction<string>>;
  selectableEntities: CustomerJobFilterSelectableEntities;
  setResetPagination: Dispatch<SetStateAction<boolean>>;
}) => {
  const [newFilter, setNewFilter] = useState(false);
  const [activeFilter, setActiveFilter] = useState(false);
  const [doneFilter, setDoneFilter] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = useState(searchParams.get('textSearch') || '');
  const [firstRender, setFirstRender] = useState(true);

  const [filtersChanged, setFiltersChanged] = useState(false);
  useEffect(() => {
    if (!firstRender) return;

    const statusFilter = searchParams.get('statusFilter') || 'ALL';
    const textSearch = searchParams.get('textSearch') || '';
    const user = searchParams.get('assignedCustomerUserId') || '';

    setNewFilter(statusFilter === 'NEW');
    setActiveFilter(statusFilter === 'ACTIVE');
    setDoneFilter(statusFilter === 'DONE');
    setSearchText(textSearch);
    setSelectedUser(user);
    setFirstRender(false);
  }, [searchParams, firstRender]);

  useEffect(() => {
    if (firstRender) return;

    setFiltersChanged(true);
  }, [newFilter, activeFilter, doneFilter, searchText, firstRender, selectedUser]);

  // Sync state changes to `searchParams`
  useEffect(() => {
    if (firstRender) return;

    const params = new URLSearchParams();

    const statusFilter = newFilter ? 'NEW' : activeFilter ? 'ACTIVE' : doneFilter ? 'DONE' : 'ALL';

    params.set('statusFilter', statusFilter);
    if (searchText) params.set('textSearch', searchText);
    params.set('page', filtersChanged ? '1' : searchParams.get('page') || '1');
    if (selectedUser) params.set('assignedCustomerUserId', selectedUser);

    // Update URL parameters only if they differ
    const queryString = params.toString();
    setSearchQuery(queryString);
    setSearchParams(params);
    setFiltersChanged(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersChanged, setSearchParams, setSearchQuery, firstRender, searchParams]);

  return (
    <div className="flex items-center gap-4">
      <SearchInput setSearchText={setSearchText} searchText={searchText} />
      <div className="flex gap-3">
        <Button
          variant={doneFilter ? 'default' : 'outline'}
          onClick={() => {
            setDoneFilter((prev) => !prev);
            setNewFilter(false);
            setActiveFilter(false);
          }}
          className="flex gap-3 items-center rounded-2xl px-3 py-2 border text-sm ">
          <FilterIcon active={doneFilter} />
          Fertige Aufträge
        </Button>
        <Button
          variant={activeFilter ? 'default' : 'outline'}
          onClick={() => {
            setActiveFilter((prev) => !prev);
            setNewFilter(false);
            setDoneFilter(false);
          }}
          className="flex gap-3 items-center rounded-2xl px-3 py-2 border text-sm ">
          <FilterIcon active={activeFilter} />
          Offene Aufträge
        </Button>
        <Button
          variant={newFilter ? 'default' : 'outline'}
          onClick={() => {
            setNewFilter((prev) => !prev);
            setActiveFilter(false);
            setDoneFilter(false);
          }}
          className="flex gap-3 items-center rounded-2xl px-3 py-2 border text-sm ">
          <FilterIcon active={newFilter} />
          Neue Aufträge
        </Button>
      </div>
      <div className="relative">
        <Select
          onValueChange={(value) => {
            setSelectedUser(value);
            setResetPagination(true);
          }}>
          <SelectTrigger className="gap-3 h-10 items-center rounded-2xl px-3 py-2 border text-sm inline-flex justify-center whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border-input bg-background hover:bg-accent hover:text-accent-foreground">
            {/* <ChevronDown size="18" /> */}
            <SelectValue placeholder="Ansprechpartner" />
          </SelectTrigger>
          <SelectContent className="cursor-pointer">
            <SelectItem value="ALL">Alle Ansprechpartner</SelectItem>
            {selectableEntities?.customerUsers.map((user) => (
              <SelectItem key={user._id} value={user._id}>
                {user.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export const getNoOrderText = (filterString: string) => {
  const match = filterString.match(/statusFilter=(\w+)/);
  const statusFilter = match ? match[1] : 'ALL';
  switch (statusFilter) {
    case 'NEW':
      return 'Du hast noch keine neuen Aufträge';
    case 'ACTIVE':
      return 'Du hast noch keine offenen Aufträge';
    case 'DONE':
      return 'Du hast noch keine fertigen Aufträge';
    case 'ALL':
      return 'Du hast noch keine Aufträge';
    default:
      return 'Du hast noch keine Aufträge';
  }
};

export default Order;
